/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import useServiceSelection from "./useServiceSelection";
import { useEffect, useState } from "react";
import usePluginUtils from "./usePluginUtils";
import { useBooking } from "./useBooking";

const useGroupBookingCounter = ({ service, category }) => {
  const { selectedServices, membershipAccessGrants } = useBooking();
  const { updateServiceBooking } = useServiceSelection(category);
  const { isPluginEnabled } = usePluginUtils();
  const isGroupBookingEnabled = isPluginEnabled("group_booking");
  const isRecurring = service.recurring_settings;
  const defaultCounter =
    isGroupBookingEnabled && !isRecurring && service.limit_booking !== 1
      ? selectedServices.find((s) => s.id === service.id)?.group_booking_count || service.min_group_booking || 1
      : null;
  const [counter, setCounter] = useState(defaultCounter);
  const [disabled, setDisabled] = useState(false);

  // Cap attendees at the paying membership's remaining credits.
  const grant = membershipAccessGrants?.[service.id];
  const membershipCapped =
    grant && grant.clientMembershipId != null && !grant.isUnlimited && Number.isFinite(grant.rest);
  const maxCounter = membershipCapped ? Math.min(service.limit_booking, grant.rest) : service.limit_booking;

  useEffect(() => {
    if (selectedServices.find((s) => s.id !== service.id)) {
      setDisabled(true);
    } else {
      setDisabled(false);
      setCounter(defaultCounter);
    }

    if (service?.group_booking_count) {
      setCounter(service.group_booking_count);
    }
  }, [selectedServices, service.group_booking_count]);

  const handleIncrease = (e) => {
    // Enforce the cap here, not just via the button's disabled attribute.
    if (counter >= maxCounter) return;
    setCounter(counter + 1);
    return updateServiceBooking(service, counter + 1);
  };

  const handleDecrease = (e) => {
    if (counter > service.min_group_booking) {
      setCounter(counter - 1);
      return updateServiceBooking(service, counter - 1);
    }
  };

  return {
    counter,
    disabled,
    maxCounter,
    handleIncrease,
    handleDecrease,
  };
};

export default useGroupBookingCounter;
